import React from 'react'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSelector } from 'react-redux'
import { View } from 'react-native'
import { Button, Text } from '@src/components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { getCoachDescriptor, useGoBack } from '@src/utils'
import { AppStackParamList } from '@src/navigation/types'
import { userSelector } from '@src/selectors/app'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { NutritionistPhotoIcon } from '../NutritionistPhotoIcon'
import { useCallSchedulingState } from '../NextCall/useCallSchedulingState'
import { VideoCallsStartLoader } from './VideoCallsStartLoader'

export const VideoCallsStartContent = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const goBack = useGoBack()
  useLazyScreenPreload('ScheduleNextCallModal')

  const user = useSelector(userSelector)
  const { promptForInsurance, canSchedule, schedulingInfo, loading } = useCallSchedulingState()

  const coachDescriptor = getCoachDescriptor(user)
  const bookingAvailable = !!schedulingInfo?.mnt?.bookingAvailable
  const canScheduleOrBook = canSchedule || bookingAvailable
  const primaryButtonTitle = promptForInsurance ? 'Get Started' : 'Schedule your next call'

  const onGetStartedPress = () => {
    if (promptForInsurance) {
      navigation.replace('VideoCallsGettingStartedScreen')
      return
    }

    if (canSchedule) {
      navigation.replace('ScheduleNextCallModal')
      return
    }

    if (schedulingInfo?.mnt?.bookingAvailable) {
      const defaultOption = schedulingInfo.mnt.options[0]
      const { appointmentKind, product } = defaultOption
      const navigationParams: AppStackParamList['CallScheduling'] = {
        appointmentKind,
        pendingAppointmentId: schedulingInfo.mnt.pendingAppointment?.id,
        allowBackNavigation: true,
        product,
      }
      navigation.navigate('CallScheduling', navigationParams)
      return
    }

    goBack()
  }

  if (loading) {
    return <VideoCallsStartLoader />
  }

  return (
    <>
      <NutritionistPhotoIcon style={styles.nutritionistPhoto} />
      <Text style={styles.content} type="title-2">
        Video Calls with your {coachDescriptor.capitalized.toString()}!
      </Text>
      {promptForInsurance ? (
        <Text style={styles.content} type="large">
          Good news: video calls are{' '}
          <Text type="large" bold>
            $0 out of pocket
          </Text>{' '}
          with most insurance. We won't charge you anything without your consent.
        </Text>
      ) : (
        <Text style={styles.content} type="large">
          {canScheduleOrBook
            ? 'Find a time that works for you.'
            : 'You cannot schedule a video call at this time'}
        </Text>
      )}
      <View style={styles.actions}>
        {(promptForInsurance || canScheduleOrBook) && (
          <Button
            accessibilityLabel={primaryButtonTitle}
            type="primary"
            size="block"
            onPress={onGetStartedPress}
          >
            {primaryButtonTitle}
          </Button>
        )}
        <Button accessibilityLabel="Dismiss" type="transparent" size="block" onPress={goBack}>
          Dismiss
        </Button>
      </View>
    </>
  )
}

const themedStyles = StyleService.create({
  nutritionistPhoto: {
    alignSelf: 'center',
  },
  content: {
    textAlign: 'center',
    margin: 8,
  },
  actions: {
    marginTop: 40,
    gap: 8,
  },
})
